import { UiText } from '@experiences/ui-common';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListSubheader from '@mui/material/ListSubheader';
import Popover from '@mui/material/Popover';
import { makeStyles } from '@mui/styles';
import Tokens from '@uipath/apollo-core';
import {
    ApIcon,
    ApProgressBar,
    ApTextField,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';

import type { CodedDashboard } from '../../../../services/governanceV2/CodedDashboardsService';
import type { PinSource } from '../defaultPage/templateDeploy/types';
import { useInfiniteScrollSentinel } from '../hooks/useInfiniteScrollSentinel';
import { useTogglePinnedDashboard } from '../hooks/useTogglePinnedDashboard';
import { DASHBOARD_TAGS } from '../util/constants';
import { useDashboardPicker } from './hooks/useDashboardPicker';

/** Long enough that typing a word is one request. */
const SEARCH_DEBOUNCE_MS = 350;

const useStyles = makeStyles(theme => ({
    paper: {
        marginTop: Tokens.Spacing.SpacingXs,
        borderRadius: '12px',
        border: `${Tokens.Stroke.StrokeS} solid ${theme.palette.semantic.colorBorderDeEmp}`,
        boxShadow: Tokens.Shadow.ShadowMenuElevation,
        overflow: 'hidden',
    },
    list: {
        minWidth: '256px',
        maxHeight: '320px',
        overflowY: 'auto',
    },
    header: {
        textTransform: 'uppercase',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.04em',
        lineHeight: '40px',
        color: theme.palette.semantic.colorForegroundDeEmp,
        borderBottom: `${Tokens.Stroke.StrokeS} solid ${theme.palette.semantic.colorBorderDeEmp}`,
    },
    checkboxIcon: {
        width: Tokens.Spacing.SpacingBase,
        height: Tokens.Spacing.SpacingBase,
        borderRadius: Tokens.Border.BorderRadiusM,
        border: `${Tokens.Stroke.StrokeS} solid ${theme.palette.semantic.colorBorder}`,
        boxSizing: 'border-box',
    },
    checkboxIconChecked: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: Tokens.Spacing.SpacingBase,
        height: Tokens.Spacing.SpacingBase,
        borderRadius: Tokens.Border.BorderRadiusM,
        backgroundColor: theme.palette.primary.main,
        boxSizing: 'border-box',
    },
    checkmark: {
        fontSize: '11px',
        color: theme.palette.primary.contrastText,
    },
    search: {
        display: 'block',
        padding: `${Tokens.Spacing.SpacingXs} ${Tokens.Spacing.SpacingBase} 0`,
    },
    emptyState: {
        display: 'block',
        padding: Tokens.Spacing.SpacingBase,
        color: theme.palette.semantic.colorForegroundDeEmp,
    },
    progressBar: {
        width: '100%',
        padding: Tokens.Spacing.SpacingBase,
    },
}));

interface PinDashboardsMenuProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    /** Which affordance opened this menu; the nav and the getting-started banner both render it. */
    source: PinSource;
}

const PinDashboardsMenu: React.FC<PinDashboardsMenuProps> = ({
    anchorEl, onClose, source,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const [ query, setQuery ] = useState('');
    const [ debouncedQuery ] = useDebounce(query, SEARCH_DEBOUNCE_MS);
    const isOpen = Boolean(anchorEl);
    const {
        dashboards, isLoading, hasMore, loadMore, refresh: refreshPicker,
    } = useDashboardPicker(debouncedQuery, isOpen);
    const {
        pendingId, togglePin,
    } = useTogglePinnedDashboard(source);

    // The checkbox reads `tags` off these rows, so they have to be re-read or it snaps back unchanged.
    const handleTogglePin = useCallback(async (dashboard: CodedDashboard, shouldPin: boolean) => {
        if (await togglePin(dashboard, shouldPin)) {
            await refreshPicker();
        }
    }, [ refreshPicker, togglePin ]);

    const handleSearch = useCallback(
        (event: CustomEvent<string>) => setQuery(event.detail ?? ''),
        [],
    );

    // Pages as the list is scrolled rather than draining: this lists every dashboard in the tenant.
    const sentinelRef = useInfiniteScrollSentinel({
        hasMore,
        loadMore,
    });

    return (
        <Popover
            open={isOpen}
            anchorEl={anchorEl}
            onClose={onClose}
            PaperProps={{ className: classes.paper }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            {/* Uncontrolled: the wrapper reassigns `node.value` every render, which wipes a keystroke
              * if a re-render lands before the state update. */}
            <ApTextField
                className={classes.search}
                size="small"
                placeholder={translate({ id: 'CLIENT_GOVERNANCE_V2_LIBRARY_SEARCH_DASHBOARDS' })}
                onValueChanged={handleSearch}
                data-cy="governance-v2-pin-dashboards-search" />
            <List
                className={classes.list}
                disablePadding
                data-cy="governance-v2-pin-dashboards-list"
                subheader={
                    <ListSubheader
                        component="div"
                        disableSticky
                        className={classes.header}
                    >
                        {translate({ id: 'CLIENT_GOVERNANCE_V2_NAV_PIN_DASHBOARDS_TITLE' })}
                    </ListSubheader>
                }
            >
                {isLoading ? (
                    <ListItem
                        disablePadding
                        data-cy="governance-v2-pin-dashboards-loading">
                        <ApProgressBar className={classes.progressBar} />
                    </ListItem>
                ) : null}
                {!isLoading && dashboards.length === 0 ? (
                    <ListItem disablePadding>
                        <UiText className={classes.emptyState}>
                            {translate({ id: 'CLIENT_GOVERNANCE_V2_NAV_NO_DASHBOARDS' })}
                        </UiText>
                    </ListItem>
                ) : null}
                {dashboards.map(dashboard => {
                    const isPinned = dashboard.tags.includes(DASHBOARD_TAGS.PINNED);
                    return (
                        <ListItem
                            key={dashboard.id}
                            disablePadding
                        >
                            <ListItemButton
                                dense
                                disabled={pendingId === dashboard.id}
                                onClick={() => void handleTogglePin(dashboard, !isPinned)}
                                data-cy={`governance-v2-pin-dashboard-${dashboard.id}`}
                            >
                                <Checkbox
                                    edge="start"
                                    checked={isPinned}
                                    tabIndex={-1}
                                    disableRipple
                                    icon={<span className={classes.checkboxIcon} />}
                                    checkedIcon={
                                        <span className={classes.checkboxIconChecked}>
                                            <ApIcon
                                                name="check"
                                                size="11px"
                                                ariaHidden="true"
                                                className={classes.checkmark} />
                                        </span>
                                    }
                                />
                                <UiText>
                                    {dashboard.deploymentTitle}
                                </UiText>
                            </ListItemButton>
                        </ListItem>
                    );
                })}
                {/* Pulls the next page once it scrolls into view. */}
                {hasMore ? <div ref={sentinelRef} /> : null}
            </List>
        </Popover>
    );
};

export default PinDashboardsMenu;
